/**
 * Team-level aggregate funnel metrics with trend hints and a proportional breakdown bar.
 *
 * Where: `TeamDetails` overview next to stream cards.
 * Uses: `ProportionalProgressBar`, `StreamDetails['statistics']` shape.
 * Behavior: responsive layout; loading skeleton when `isLoading`.
 */
import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  HelpOutline as HelpOutlineIcon,
  BarChart as BarChartIcon,
} from '@mui/icons-material';
import { ProportionalProgressBar } from '@/components';
import { StreamDetails } from '@/api/stream';

interface AggregatedStatsCardProps {
  statistics: StreamDetails['statistics'];
  isLoading?: boolean;
}

const HintIcon: React.FC<{ title: string }> = ({ title }) => (
  <Tooltip title={title} placement="top">
    <HelpOutlineIcon sx={{ fontSize: '0.75rem', color: 'text.secondary', ml: 0.5, cursor: 'help' }} />
  </Tooltip>
);

const TrendIndicator: React.FC<{ current: number; previous: number }> = ({ current, previous }) => {
  if (current > previous) {
    return <TrendingUpIcon sx={{ fontSize: '0.875rem', color: 'success.main', ml: 0.5 }} />;
  }
  if (current < previous) {
    return <TrendingDownIcon sx={{ fontSize: '0.875rem', color: 'error.main', ml: 0.5 }} />;
  }
  return <TrendingFlatIcon sx={{ fontSize: '0.875rem', color: 'text.secondary', ml: 0.5 }} />;
};

const PROGRESS_BAR_CATEGORIES: string[] = [
  'Queuing for import',
  'Checking if already connected',
  'Sending invite',
  'Pending acceptance',
  'Queuing for follow-up',
];

// Helper function to generate progress segments from statistics.
// Always returns all 4 categories (with 0 for missing) so numbers are always shown.
const generateProgressSegments = (statistics: StreamDetails['statistics']) => {
  const sectionMap = new Map<string, { count: number; color?: string }>();
  if (statistics?.progress_sections) {
    for (const section of statistics.progress_sections) {
      if (PROGRESS_BAR_CATEGORIES.includes(section.category)) {
        sectionMap.set(section.category, {
          count: section.count ?? 0,
          color: section.color,
        });
      }
    }
  }
  return PROGRESS_BAR_CATEGORIES.map((category) => {
    const data = sectionMap.get(category);
    const value = data?.count ?? 0;
    return {
      value,
      description: `${category}: ${value} prospects`,
      color: data?.color,
    };
  });
};

// Helper function to get statistics in a convenient form
const getStats = (statistics: StreamDetails['statistics']) => {
  const s = statistics || {};
  const totalLeads = s.total_leads || 0;
  const acceptanceRate = (s.acceptance_rate || 0) / 100; // convert to 0-1
  const responseRate = (s.response_rate || 0) / 100; // convert to 0-1
  const acceptedConnections = s.accepted_connections || 0;
  const messagesReceived = s.messages_received || 0;

  return {
    totalLeads,
    scheduledMeetings: s.scheduled_meetings || 0,
    scheduledMeetingsSinceYesterday: s.scheduled_meetings_since_yesterday || 0,
    meetingAcceptedCount: s.meeting_accepted_count || 0,
    sentMessages: s.sent_messages || 0,
    acceptanceRate,
    acceptedConnections,
    responseRate,
    messagesReceived,
    failedJobs: s.failed_jobs || 0,
    failedJobsYesterday: s.failed_jobs_yesterday || 0,
    prospectEstimationDays: s.prospect_estimation_days || 0,
    sentInvites: s.sent_invites || 0,
    numberOfStreams: s.number_of_streams || 0,
    numberOfOperators: s.number_of_operators || 0,
  };
};

export const AggregatedStatsCard: React.FC<AggregatedStatsCardProps> = ({ statistics, isLoading = false }) => {
  const stats = getStats(statistics);
  const progressSegments = generateProgressSegments(statistics);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (isLoading) {
    return (
      <Card sx={{ p: 2, mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              Loading aggregated statistics...
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (isMobile) {
    return (
      <Card sx={{ p: 2, mb: 2 }}>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <BarChartIcon sx={{ color: 'primary.main' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Aggregated Statistics
            </Typography>
          </Box>

          {/* Progress Bar */}
          <Box sx={{ mb: 2 }}>
            {progressSegments.length > 0 ? (
              <ProportionalProgressBar segments={progressSegments} height={6} gap={3} showValues={true} />
            ) : (
              <Box sx={{ height: 6, bgcolor: 'grey.100', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  No progress data available
                </Typography>
              </Box>
            )}
          </Box>

          {/* Lead Statistics */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  Number of experiments
                </Typography>
                <HintIcon title="Total number of experiments included in this aggregation" />
              </Box>
              <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500, fontSize: '0.875rem' }}>
                {stats.numberOfStreams}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  Number of operators
                </Typography>
                <HintIcon title="Total number of distinct operators across all filtered experiments" />
              </Box>
              <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500, fontSize: '0.875rem' }}>
                {stats.numberOfOperators}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  All leads
                </Typography>
                <HintIcon title="Total number of people in your prospect list across all filtered experiments" />
              </Box>
              <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 500, fontSize: '0.875rem' }}>
                {stats.totalLeads}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  Failed jobs
                </Typography>
                <HintIcon title={`Prospects that failed to process (${stats.failedJobsYesterday} failed yesterday)`} />
              </Box>
              <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 500, fontSize: '0.875rem' }}>
                {stats.failedJobs}
              </Typography>
            </Box>
            {/* Estimated days remaining - commented out for aggregated results */}
            {/* <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  Estimated days remaining
                </Typography>
                <HintIcon title="Estimated number of days until all prospects are processed" />
              </Box>
              <Typography variant="body2" sx={{ color: 'warning.main', fontWeight: 500, fontSize: '0.875rem' }}>
                {Math.round(stats.prospectEstimationDays)} days
              </Typography>
            </Box> */}
          </Box>

          {/* Daily Trend Statistics */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  Sent connections
                </Typography>
                <HintIcon title="Total connection requests sent" />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ color: 'warning.main', fontWeight: 500, fontSize: '0.875rem', mr: 1 }}>
                  {stats.sentInvites}
                </Typography>
                <TrendIndicator
                  current={statistics?.sent_invites_yesterday || 0}
                  previous={statistics?.sent_invites_day_before || 0}
                />
                <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                  ({statistics?.sent_invites_yesterday || 0})
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  Accepted connections
                </Typography>
                <HintIcon title={`People who accepted your connection requests (${statistics?.accepted_connections_yesterday || 0} yesterday)`} />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 500, fontSize: '0.875rem', mr: 1 }}>
                  {stats.acceptedConnections} ({(stats.acceptanceRate * 100).toFixed(1)}%)
                </Typography>
                <TrendIndicator
                  current={statistics?.accepted_connections_yesterday || 0}
                  previous={statistics?.accepted_connections_day_before || 0}
                />
                <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                  ({statistics?.accepted_connections_yesterday || 0})
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  Responded messages
                </Typography>
                <HintIcon title={`Messages received from prospects (${statistics?.positive_responses_yesterday || 0} yesterday)`} />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ color: 'info.main', fontWeight: 500, fontSize: '0.875rem', mr: 1 }}>
                  {stats.messagesReceived} ({(stats.responseRate * 100).toFixed(1)}%)
                </Typography>
                <TrendIndicator
                  current={statistics?.positive_responses_yesterday || 0}
                  previous={statistics?.positive_responses_day_before || 0}
                />
                <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                  ({statistics?.positive_responses_yesterday || 0})
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  Accepted meetings
                </Typography>
                <HintIcon title="Chats tagged by AI as &quot;Meeting Accepted&quot;." />
              </Box>
              <Typography variant="body2" sx={{ color: 'info.main', fontWeight: 500, fontSize: '0.875rem' }}>
                {stats.meetingAcceptedCount} ({stats.messagesReceived > 0 ? ((stats.meetingAcceptedCount / stats.messagesReceived) * 100).toFixed(1) : '0'}%)
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  Scheduled meetings
                </Typography>
                <HintIcon title={`Total meetings scheduled (${stats.scheduledMeetingsSinceYesterday} new yesterday) - ${stats.messagesReceived > 0 ? ((stats.scheduledMeetings / stats.messagesReceived) * 100).toFixed(1) : '0'}% of responded messages`} />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 500, fontSize: '0.875rem', mr: 1 }}>
                  {stats.scheduledMeetings} ({stats.messagesReceived > 0 ? ((stats.scheduledMeetings / stats.messagesReceived) * 100).toFixed(1) : '0'}%)
                </Typography>
                <TrendIndicator
                  current={stats.scheduledMeetingsSinceYesterday}
                  previous={0}
                />
                <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                  ({stats.scheduledMeetingsSinceYesterday})
                </Typography>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Desktop layout
  return (
    <Card sx={{ p: 2, mb: 2 }}>
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <BarChartIcon sx={{ color: 'primary.main' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Aggregated Statistics
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 3 }}>
          {/* Progress Bar */}
          <Box sx={{ flex: '0 0 33%', maxWidth: '33%' }}>
            <Box sx={{ width: '100%', mb: 2 }}>
              {progressSegments.length > 0 ? (
                <ProportionalProgressBar
                  segments={progressSegments}
                  height={6}
                  gap={3}
                  showValues={true}
                />
              ) : (
                <Box sx={{
                  height: 6,
                  bgcolor: 'grey.100',
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Typography variant="caption" color="text.secondary">
                    No progress data available
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Quick Stats */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 120 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  Number of experiments
                </Typography>
                <HintIcon title="Total number of experiments included in this aggregation" />
              </Box>
              <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500, fontSize: '0.875rem', ml: 1 }}>
                {stats.numberOfStreams}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  Number of operators
                </Typography>
                <HintIcon title="Total number of distinct operators across all filtered experiments" />
              </Box>
              <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500, fontSize: '0.875rem', ml: 1 }}>
                {stats.numberOfOperators}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  All leads
                </Typography>
                <HintIcon title="Total number of people in your prospect list across all filtered experiments" />
              </Box>
              <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 500, fontSize: '0.875rem', ml: 1 }}>
                {stats.totalLeads}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  Failed jobs
                </Typography>
                <HintIcon title={`Prospects that failed to process (${stats.failedJobsYesterday} failed yesterday)`} />
              </Box>
              <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 500, fontSize: '0.875rem', ml: 1 }}>
                {stats.failedJobs}
              </Typography>
            </Box>
            {/* Estimated days remaining - commented out for aggregated results */}
            {/* <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  Estimated days remaining
                </Typography>
                <HintIcon title="Estimated number of days until all prospects are processed" />
              </Box>
              <Typography variant="body2" sx={{ color: 'warning.main', fontWeight: 500, fontSize: '0.875rem', ml: 1 }}>
                {Math.round(stats.prospectEstimationDays)} days
              </Typography>
            </Box> */}
          </Box>

          {/* Daily Trend Statistics */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 180 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  Sent connections
                </Typography>
                <HintIcon title="Total connection requests sent" />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ color: 'warning.main', fontWeight: 500, fontSize: '0.875rem', mr: 1 }}>
                  {stats.sentInvites}
                </Typography>
                <TrendIndicator
                  current={statistics?.sent_invites_yesterday || 0}
                  previous={statistics?.sent_invites_day_before || 0}
                />
                <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                  ({statistics?.sent_invites_yesterday || 0})
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  Accepted connections
                </Typography>
                <HintIcon title={`People who accepted your connection requests (${statistics?.accepted_connections_yesterday || 0} yesterday)`} />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 500, fontSize: '0.875rem', mr: 1 }}>
                  {stats.acceptedConnections} ({(stats.acceptanceRate * 100).toFixed(1)}%)
                </Typography>
                <TrendIndicator
                  current={statistics?.accepted_connections_yesterday || 0}
                  previous={statistics?.accepted_connections_day_before || 0}
                />
                <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                  ({statistics?.accepted_connections_yesterday || 0})
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  Responded messages
                </Typography>
                <HintIcon title={`Messages received from prospects (${statistics?.positive_responses_yesterday || 0} yesterday)`} />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ color: 'info.main', fontWeight: 500, fontSize: '0.875rem', mr: 1 }}>
                  {stats.messagesReceived} ({(stats.responseRate * 100).toFixed(1)}%)
                </Typography>
                <TrendIndicator
                  current={statistics?.positive_responses_yesterday || 0}
                  previous={statistics?.positive_responses_day_before || 0}
                />
                <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                  ({statistics?.positive_responses_yesterday || 0})
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  Accepted meetings
                </Typography>
                <HintIcon title="Chats tagged by AI as &quot;Meeting Accepted&quot;." />
              </Box>
              <Typography variant="body2" sx={{ color: 'info.main', fontWeight: 500, fontSize: '0.875rem', ml: 1 }}>
                {stats.meetingAcceptedCount} ({stats.messagesReceived > 0 ? ((stats.meetingAcceptedCount / stats.messagesReceived) * 100).toFixed(1) : '0'}%)
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  Scheduled meetings
                </Typography>
                <HintIcon title={`Total meetings scheduled (${stats.scheduledMeetingsSinceYesterday} new yesterday) - ${stats.messagesReceived > 0 ? ((stats.scheduledMeetings / stats.messagesReceived) * 100).toFixed(1) : '0'}% of responded messages`} />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 500, fontSize: '0.875rem', mr: 1 }}>
                  {stats.scheduledMeetings} ({stats.messagesReceived > 0 ? ((stats.scheduledMeetings / stats.messagesReceived) * 100).toFixed(1) : '0'}%)
                </Typography>
                <TrendIndicator
                  current={stats.scheduledMeetingsSinceYesterday}
                  previous={0}
                />
                <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                  ({stats.scheduledMeetingsSinceYesterday})
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};
